import {
  BznavAppIds,
  getCookieData,
  setCookieData,
  BZNAV_ACCESS_TOKEN,
  BZNAV_USER_DATA,
  getSessionId,
  getClientSessionId,
  AUTH_COOKIE_DOMAIN,
  deleteCookieData
} from '@repo/common-utils';
import CryptoJS from 'crypto-js';

import { SessionUser, SignPaths } from '../types';
import { getSavedReturnUrl } from '../utils';

/**
 * 인증/세션 서비스의 공통 기능을 제공하는 베이스 클래스
 */
export abstract class BaseAuthService {
  protected _appId: BznavAppIds;
  protected _clientId: string;
  protected _clientSecret: string;
  protected _signPaths: SignPaths;
  protected _authSeverUrl: string;
  protected _encryptedUserData?: string;
  protected _decryptedUserData?: SessionUser;

  constructor(appId: BznavAppIds, clientId: string, clientSecret: string, signPaths: SignPaths, authSeverUrl: string) {
    this._appId = appId;
    this._clientId = clientId;
    this._clientSecret = clientSecret;
    this._signPaths = signPaths;
    this._authSeverUrl = authSeverUrl;
  }

  get appId(): BznavAppIds {
    return this._appId;
  }

  get clientId(): string {
    return this._clientId;
  }

  get sessionId(): string {
    return getSessionId();
  }

  get clientSessionId(): string {
    return getClientSessionId();
  }

  get accessToken(): string {
    return getCookieData(BZNAV_ACCESS_TOKEN) ?? '';
  }

  set accessToken(token: string) {
    setCookieData(BZNAV_ACCESS_TOKEN, token, this.authCookieOptions);
  }

  get userData(): SessionUser | null {
    try {
      if (this._decryptedUserData) return this._decryptedUserData;
      if (!this._encryptedUserData) {
        const savedUserData = getCookieData(BZNAV_USER_DATA);
        if (!savedUserData) return null;
        this._encryptedUserData = savedUserData;
      }
      this._decryptedUserData = JSON.parse(
        CryptoJS.AES.decrypt(this._encryptedUserData as string, this._clientSecret).toString(CryptoJS.enc.Utf8)
      ) as SessionUser;
    } catch {
      deleteCookieData(BZNAV_USER_DATA, this.authCookieOptions);
      this._decryptedUserData = undefined;
    }
    return this._decryptedUserData || null;
  }

  get signPaths() {
    return this._signPaths;
  }

  set userData(userData: SessionUser) {
    const plainUserData = JSON.stringify(Object.assign({}, this.userData, userData));
    const encryptedUserData = CryptoJS.AES.encrypt(plainUserData, this._clientSecret).toString();
    this._encryptedUserData = encryptedUserData;
    this._decryptedUserData = undefined;
    setCookieData(BZNAV_USER_DATA, encryptedUserData, this.authCookieOptions);
  }

  get signInSuccessUrl(): string {
    return getSavedReturnUrl() || this._signPaths.defaultReturn;
  }

  protected get authCookieOptions() {
    return {
      domain: AUTH_COOKIE_DOMAIN,
      path: '/',
      expires: new Date(Date.now() + 60 * 60 * 24 * 1000 * (this._clientId.endsWith('-app') ? 30 : 1)),
      secure: process.env.NEXT_PUBLIC_ZENV !== 'loc'
    };
  }
}
